'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { Proposal } from '@/types/customer';
import { PortalLayout } from '../PortalLayout';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';

const STATUS_COLOURS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-amber-100 text-amber-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-gray-100 text-gray-500',
};

export function MyProposals() {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);

  const fetchProposals = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      setLoading(true);
      const data = await client.getProposals();
      setProposals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[MyProposals] Failed to fetch proposals:', err);
    } finally {
      setLoading(false);
    }
  }, [getCustomerClient]);

  useEffect(() => { if (isAuthenticated) fetchProposals(); }, [isAuthenticated, fetchProposals]);

  const handleViewProposal = async (proposal: Proposal) => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      const detail = await client.getProposal(proposal.id);
      setSelectedProposal(detail);
      await client.trackProposalEngagement({
        proposalId: proposal.id,
        eventType: 'VIEW',
      });
    } catch (err) {
      console.warn('[MyProposals] Failed to load proposal:', err);
    }
  };

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Proposals</h1>
            <p className="text-gray-600 mt-1">View and respond to proposals from your agent</p>
          </div>
        </div>

        {selectedProposal ? (
          <div className="space-y-4">
            <button
              onClick={() => setSelectedProposal(null)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back to proposals
            </button>
            <Card className="p-6">
              {selectedProposal.coverImageUrl && (
                <div className="relative h-48 bg-gray-100 rounded-lg mb-6 overflow-hidden">
                  <Image src={selectedProposal.coverImageUrl} alt="" fill className="object-cover" unoptimized />
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-xl font-bold text-gray-900">{selectedProposal.title}</h2>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOURS[selectedProposal.status] || 'bg-gray-100 text-gray-700'}`}>
                  {selectedProposal.status}
                </span>
              </div>
              {selectedProposal.agentName && (
                <p className="text-sm text-gray-600 mb-4">
                  From: <span className="font-medium">{selectedProposal.agentName}</span>
                  {selectedProposal.organisationName && ` at ${selectedProposal.organisationName}`}
                </p>
              )}
              {selectedProposal.summary && (
                <p className="text-gray-700 mb-6">{selectedProposal.summary}</p>
              )}
              {selectedProposal.parts && selectedProposal.parts.length > 0 && (
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-gray-900">Sections</h3>
                  {selectedProposal.parts.map((part) => (
                    <div key={part.id} className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900">{part.title}</h4>
                      {part.content && (
                        <div className="mt-2 text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: part.content }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        ) : loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : proposals.length === 0 ? (
          <Card className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No proposals yet</h3>
            <p className="text-gray-500 text-sm">Proposals from your agent will appear here</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {proposals.map((proposal) => (
              <Card key={proposal.id} className="p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewProposal(proposal)}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{proposal.title}</h3>
                      <Badge className={STATUS_COLOURS[proposal.status] || ''}>{proposal.status}</Badge>
                    </div>
                    {proposal.agentName && (
                      <p className="text-sm text-gray-500">From {proposal.agentName}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(proposal.createdAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
